import type { Project } from '@sdk';
import { Button } from '@src/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@src/components/ui/popover';
import { Flag, LocalePicker } from '@src/components/locale/LocalePicker';
import { DEFAULT_LOCALE, setProjectLocale, useSupportedLocales } from '@src/contexts/locale-context';
import { ChevronDown, Languages } from 'lucide-react';
import React, { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

interface ProjectLanguageCardProps {
  project: Project;
}

/**
 * The project's language setting — "which language is this project read in",
 * for every member.
 *
 * This card is the ONLY writer of the shared `project.locale` (`setProjectLocale`):
 * the footer chip changes just this device's language, so a stray footer switch
 * never changes the project for everyone. This card only renders for the ACTIVE
 * project — ProjectHome gates its entity-bound cards on that.
 */
export const ProjectLanguageCard: React.FC<ProjectLanguageCardProps> = ({ project }) => {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const supportedLocales = useSupportedLocales();

  // The project's stored language. A project that has never been given one is
  // read in English — `applyProjectLocale` opens it that way — so unset shows as
  // English here rather than as whatever the app happens to be showing now.
  const selectedCode = supportedLocales.some((l) => l.code === project.locale)
    ? (project.locale as string)
    : DEFAULT_LOCALE;
  const selectedInfo = supportedLocales.find((l) => l.code === selectedCode) ?? supportedLocales[0];

  const handleSelect = (code: string) => {
    setOpen(false);
    void setProjectLocale(project, code);
  };

  // Nothing to choose between when the backend ships a single locale — same
  // rule as the footer chip.
  if (supportedLocales.length < 2 || !selectedInfo) return null;

  return (
    <div className="rounded-lg border border-border p-4" data-testid="project-language-card">
      <div className="mb-3 flex items-center gap-2">
        <Languages className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-medium">
          <Trans>Language</Trans>
        </h3>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        <Trans>
          The language this project is worked in, for everyone in it. To change only your own language, use the language
          picker in the footer.
        </Trans>
      </p>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="justify-start gap-2"
            aria-label={t`Change this project's language`}
            data-testid="project-language-trigger"
          >
            <Flag code={selectedInfo.flag} className="text-base" />
            <span>{selectedInfo.nativeName}</span>
            <ChevronDown className="ms-auto h-4 w-4 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-0">
          <LocalePicker selectedCode={selectedCode} onSelect={handleSelect} />
        </PopoverContent>
      </Popover>
    </div>
  );
};
